import { Box, Button } from '@mui/material';
import { DataGridPro, DataGridProProps, GridCellParams, GridRenderCellParams, GridColDef } from '@mui/x-data-grid-pro';
import React from 'react';
import { switchMap, tap } from 'rxjs';

import { BrandAttributes } from 'app/model';
import { Brand } from 'app/model/cris/brand';
import { attributeService } from 'app/services/brand/attribute.service';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';

import { ReTagDialog } from './retag-dialog';

interface AttributeGridProps extends Omit<DataGridProProps<BrandAttributes>, 'columns'> {
    rows: BrandAttributes[];
    brand: Brand;
    onBrandUpdate: (brand: Brand) => void;
}

interface AttributeGridState {
    reTagForm: boolean;
    selectedAttribute?: BrandAttributes;
}

export class AttributeGrid extends React.Component<AttributeGridProps, AttributeGridState> {
    public constructor(props: AttributeGridProps) {
        super(props);
        this.state = {
            reTagForm: false,
            selectedAttribute: undefined,
        };
    }

    public handleReTagClick = (attribute: BrandAttributes): void => {
        this.setState({
            reTagForm: true,
            selectedAttribute: attribute,
        });
    };

    public handleReTagSave = (attribute: BrandAttributes, details: string): void => {
        attributeService
            .patch({ id: attribute.id }, { details }, this.props.brand)
            .pipe(
                switchMap(() => attributeService.listAll(undefined, this.props.brand)),
                tap((attributes) => {
                    this.props.onBrandUpdate({
                        ...this.props.brand,
                        attributes,
                    });
                    this.setState({
                        reTagForm: false,
                        selectedAttribute: undefined,
                    });
                }),
            )
            .subscribe();
    };

    public render(): React.ReactNode {
        const columns: Array<GridColDef<BrandAttributes>> = [
            {
                field: 'id',
                headerName: 'ID',
                width: 150,
            },
            {
                field: 'name',
                headerName: 'Name',
                width: 250,
            },
            {
                field: 'value',
                headerName: 'Value',
                width: 100,
                disableColumnMenu: true,
                editable: true,
            },
            {
                field: 'dateEdited',
                headerName: 'Date',
                width: 200,
                type: 'date',
                valueFormatter: DataGridFormatters.dateFormatter,
            },
            {
                field: 'details',
                headerName: 'Details',
                width: 200,
                editable: true,
            },
            {
                field: 'actions',
                headerName: 'Actions',
                width: 120,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams<BrandAttributes>) => {
                    if ('MIDMATCH_VERIFIED' === params.row.name) {
                        return (
                            <Button
                                variant='outlined'
                                size='small'
                                onClick={(event) => {
                                    event.stopPropagation();
                                    this.handleReTagClick(params.row);
                                }}
                            >
                                Re-tag
                            </Button>
                        );
                    }

                    return params.value as React.ReactNode;
                },
            },
        ];

        return (
            <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                <DataGridPro
                    {...this.props}
                    columns={columns}
                    isCellEditable={(params: GridCellParams<BrandAttributes>) => {
                        if ('details' === params.field) {
                            return 'MIDMATCH_VERIFIED' !== params.row.name;
                        }

                        return true;
                    }}
                />
                <ReTagDialog
                    open={this.state.reTagForm}
                    attribute={this.state.selectedAttribute}
                    onClose={() => this.setState({ reTagForm: false, selectedAttribute: undefined })}
                    onReTag={this.handleReTagSave}
                />
            </Box>
        );
    }
}
